import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { photoFileUrl } from "@/lib/gallery";
import { fmtDate, formatApiError } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { FolderPlus, Images, Loader2, X, Save, Sparkles, ChevronRight } from "lucide-react";

const emptyForm = {
  name: "",
  couple_name: "",
  event_date: "",
  description: "",
  is_public: true,
};

export default function GalleryList() {
  const { user } = useAuth();
  const canWrite = user?.role !== "viewer"; // iter120: admin + manager + coordinator all get write access
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery/events");
      setEvents(data);
    } catch (e) {
      logger.debug("gallery events load failed:", e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setErr("");
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      await api.post("/gallery/events", form);
      setShowForm(false);
      load();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="gallery-list-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-rose-700 font-semibold mb-1">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
            AI Gallery Manager
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Photo galleries</h1>
          <p className="text-stone-500 text-sm mt-1">
            Organise delivered shoots by event folder — Haldi, Mehndi, Wedding, and more.
          </p>
        </div>
        {canWrite && (
          <button className="btn btn-primary" onClick={openCreate} data-testid="new-gallery-event-btn">
            <FolderPlus className="w-4 h-4" strokeWidth={1.6} /> New event folder
          </button>
        )}
      </div>

      {loading ? (
        // iter147 — Skeleton grid so the layout stays stable while
        // galleries hydrate.
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-list-loading" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card overflow-hidden">
              <div className="kb-skeleton aspect-[4/3] w-full" />
              <div className="p-4 space-y-2">
                <div className="kb-skeleton h-4 w-3/4" />
                <div className="kb-skeleton h-3 w-1/2" />
              </div>
            </div>
          ))}
          <span className="sr-only">Loading galleries…</span>
        </div>
      ) : events.length === 0 ? (
        <div className="card p-16 text-center kb-fade-in" data-testid="gallery-list-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-stone-50 border border-stone-200 flex items-center justify-center mb-4">
            <Images className="w-6 h-6 text-stone-400" strokeWidth={1.6} aria-hidden="true" />
          </div>
          <div className="font-heading text-lg font-semibold text-stone-800">No galleries yet</div>
          <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto leading-relaxed">
            Create your first event folder — for example &ldquo;Haldi&rdquo; or &ldquo;Wedding&rdquo; — and drop in your delivered photos.
          </p>
          {canWrite && (
            <button className="btn btn-primary mt-6 kb-tap" onClick={openCreate} data-testid="new-gallery-event-empty-btn">
              <FolderPlus className="w-4 h-4" strokeWidth={1.6} /> Create your first folder
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {events.map((ev) => (
            <Link
              key={ev.id}
              to={`/gallery/${ev.id}`}
              className="group relative flex flex-col rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
              data-testid={`gallery-event-card-${ev.id}`}
            >
              <div className="aspect-[4/5] bg-stone-100 relative overflow-hidden">
                {ev.cover_photo_id ? (
                  <img
                    src={photoFileUrl(ev.cover_photo_id)}
                    alt={ev.name}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-300 bg-gradient-to-br from-stone-50 to-stone-100">
                    <Images className="w-14 h-14" strokeWidth={1} />
                  </div>
                )}
                {ev.photo_count > 0 && (
                  <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[10px] font-medium tracking-wider uppercase px-2 py-1 rounded-full">
                    {ev.photo_count} · {ev.photo_count === 1 ? "photo" : "photos"}
                  </div>
                )}
                {ev.is_public && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-stone-900 text-[10px] font-semibold tracking-widest uppercase px-2 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Public
                  </div>
                )}
              </div>
              <div className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-base font-semibold text-stone-900 truncate">{ev.name}</div>
                  {ev.couple_name && (
                    <div className="text-xs text-stone-500 mt-0.5 truncate italic">{ev.couple_name}</div>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-[10.5px] tracking-wider uppercase text-stone-400">
                    {ev.event_date && <span className="font-mono">{fmtDate(ev.event_date)}</span>}
                    {ev.sub_event_count > 0 && (
                      <span>· {ev.sub_event_count} sub-event{ev.sub_event_count === 1 ? "" : "s"}</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-300 mt-1 shrink-0 group-hover:text-stone-900 group-hover:translate-x-0.5 transition-all" strokeWidth={1.8} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 fade-in" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-md rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="new-gallery-modal">
            <div className="flex items-center justify-between px-6 h-14 border-b border-stone-200">
              <div className="flex items-center gap-2">
                <FolderPlus className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
                <h2 className="font-heading font-semibold">New event folder</h2>
              </div>
              <button className="btn-ghost p-2 rounded" onClick={() => setShowForm(false)}>
                <X className="w-5 h-5" strokeWidth={1.6} />
              </button>
            </div>
            <form onSubmit={save} className="p-6 space-y-3">
              <div>
                <label className="label">Event name *</label>
                <input required autoFocus className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Haldi / Mehndi / Wedding" data-testid="gallery-form-name" />
              </div>
              <div>
                <label className="label">Couple name</label>
                <input className="input" value={form.couple_name} onChange={(e) => setForm({ ...form, couple_name: e.target.value })} placeholder="Aarti & Vikram" data-testid="gallery-form-couple" />
              </div>
              <div>
                <label className="label">Event date</label>
                <input type="date" className="input" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="gallery-form-date" />
              </div>
              <div>
                <label className="label">Description (optional)</label>
                <textarea className="input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="gallery-form-description" />
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-700 pt-1">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                  data-testid="gallery-form-public"
                />
                Make this gallery browsable via public link
              </label>
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving} data-testid="gallery-form-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.6} />}
                  Create folder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
